const INTERNAL_ROLES = new Set(['owner', 'admin', 'qc_officer', 'support_agent']);
const PORTAL_SUPER_ADMIN_EMAILS = new Set(['jaxon@therecordbase.com']);
export const PLAN_CATALOG = {
  artist: { label: 'Artist', price: '$0/year', royaltySplit: 90, artistLimit: 1, customBranding: false },
  musician: { label: 'Musician', price: '$10.99/year', royaltySplit: 100, artistLimit: 1, customBranding: false },
  trb_core: { label: 'TRB Core', price: 'Paid program', royaltySplit: 100, artistLimit: null, customBranding: true },
  trb_grow: { label: 'TRB Grow', price: 'Paid program', royaltySplit: 100, artistLimit: null, customBranding: true },
  trb_select: { label: 'TRB Select', price: 'Paid program', royaltySplit: 100, artistLimit: null, customBranding: true },
  musician_pro: { label: 'Musician Pro', price: '$25.99/year', royaltySplit: 100, artistLimit: 5, customBranding: true },
  label: { label: 'Label', price: '$30.99/year', royaltySplit: 100, artistLimit: null, customBranding: true },
  api_portal: { label: 'API Portal', price: '$30.99/month', royaltySplit: 100, artistLimit: 5, customBranding: true, apiAccess: true },
  enterprise: { label: 'Enterprise', price: '$100/month', royaltySplit: 100, artistLimit: null, customBranding: true },
  partner: { label: 'Partner', price: 'Legacy', royaltySplit: 95, artistLimit: 1, customBranding: false, legacy: true },
  manager: { label: 'Artist+', price: 'Legacy', royaltySplit: 90, artistLimit: null, customBranding: false, legacy: true },
  growth: { label: 'Growth', price: 'Legacy', royaltySplit: 90, artistLimit: null, customBranding: false, legacy: true },
};

// Keep this aligned with backend/server.py's PUBLIC_SELF_SERVE_PAID_PLANS.
// API Portal is a public self-serve subscription, so it must participate in
// the same paid-subscription and entitlement checks as the other paid plans.
export const PUBLIC_PAID_PLANS = ['musician', 'musician_pro', 'label', 'api_portal'];
const SELF_SERVE_PAID_PLANS = new Set([...PUBLIC_PAID_PLANS, 'manager', 'growth', 'enterprise']);
const TEAM_ENABLED_PLANS = new Set(['trb_select', 'musician_pro', 'label', 'api_portal', 'enterprise', 'manager']);
const ROSTER_ENABLED_PLANS = new Set(['artist', 'musician', 'trb_core', 'trb_grow', 'trb_select', 'musician_pro', 'label', 'api_portal', 'enterprise', 'manager']);
const BRANDING_ENABLED_PLANS = new Set(['musician_pro', 'label', 'api_portal', 'enterprise']);
const PLAN_SORT_ORDER = ['enterprise', 'api_portal', 'label', 'musician_pro', 'trb_select', 'trb_grow', 'trb_core', 'musician', 'growth', 'manager', 'partner', 'artist'];
export const TRB_VIDEO_AVAILABLE = false;

export function formatPlanName(plan) {
  const normalized = (plan || 'artist').toString().trim().toLowerCase();
  return PLAN_CATALOG[normalized]?.label || normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function getPlanSortRank(plan) {
  const normalized = (plan || '').toString().trim().toLowerCase();
  const rank = PLAN_SORT_ORDER.indexOf(normalized);
  return rank === -1 ? PLAN_SORT_ORDER.length : rank;
}

export function getEffectivePlan(user) {
  const value = [user?.active_paid_plan, user?.plan, user?.current_plan, user?.subscription_plan, user?.plan_type]
    .find((plan) => typeof plan === 'string' && plan.trim());
  const plan = (value || '').trim().toLowerCase();
  if (SELF_SERVE_PAID_PLANS.has(plan) && !user?.has_paid_subscription) {
    return 'artist';
  }
  return plan;
}

export function isLabelTeamMember(user) {
  if (!user || INTERNAL_ROLES.has(user?.role)) return false;
  if (isWhiteLabelArtistPortalUser(user)) return false;
  return Boolean(user?.team_id);
}

export function getEntitlementPlan(user) {
  if (isLabelTeamMember(user)) return 'label';
  return getEffectivePlan(user);
}

export function getPlanComplianceRules(user) {
  const plan = getEntitlementPlan(user);
  const catalog = PLAN_CATALOG[plan] || PLAN_CATALOG.artist;
  const imprintName = catalog.imprintName || 'The Record Base';
  return {
    royaltySplit: catalog.royaltySplit,
    priorityUploadQueue: ['partner', 'api_portal', 'enterprise'].includes(plan),
    multiArtistProfiles: catalog.artistLimit !== 1,
    customPlines: Boolean(catalog.customBranding),
    imprintName,
  };
}

export function getPlanArtistLimit(user) {
  const plan = getEntitlementPlan(user);
  return (PLAN_CATALOG[plan] || PLAN_CATALOG.artist).artistLimit;
}

export function hasPaidSubscription(user) {
  return Boolean(user?.has_paid_subscription && getEffectivePlan(user) && SELF_SERVE_PAID_PLANS.has(getEffectivePlan(user)));
}

export function hasChatAccess(user) {
  if (INTERNAL_ROLES.has(user?.role) || isLabelTeamMember(user)) return true;
  if (isWhiteLabelArtistPortalUser(user)) return false;
  if (user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner) return true;
  const plan = getEntitlementPlan(user);
  if (['trb_core', 'trb_grow', 'trb_select'].includes(plan)) return true;
  return hasPaidSubscription(user);
}

export function hasBioLinksAccess(user) {
  if (INTERNAL_ROLES.has(user?.role) || isLabelTeamMember(user)) return true;
  if (isWhiteLabelArtistPortalUser(user)) return false;
  if (user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner) return true;
  const plan = getEntitlementPlan(user);
  if (['trb_core', 'trb_grow', 'trb_select'].includes(plan)) return true;
  return hasPaidSubscription(user);
}

export function hasBrandingAccess(user) {
  if (isWhiteLabelArtistPortalUser(user)) return false;
  const plan = getEntitlementPlan(user);
  if (isLabelTeamMember(user)) return true;
  if (user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner) return true;
  if (!BRANDING_ENABLED_PLANS.has(plan)) return false;
  if (SELF_SERVE_PAID_PLANS.has(plan)) {
    return hasPaidSubscription(user) && user?.active_paid_plan === plan;
  }
  return true;
}

export function hasTeamAccess(user) {
  if (isWhiteLabelArtistPortalUser(user)) return false;
  const plan = getEntitlementPlan(user);
  if (user?.team_id) return true;
  if (['label_owner', 'label_manager'].includes(user?.role)) return true;
  if (user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner) return true;
  if (!TEAM_ENABLED_PLANS.has(plan)) return false;
  if (['trb_select', 'label', 'api_portal'].includes(plan)) return true;
  if (SELF_SERVE_PAID_PLANS.has(plan)) {
    return hasPaidSubscription(user) && user?.active_paid_plan === plan;
  }
  return Boolean(user?.team_access_enabled || plan === 'enterprise');
}

export function hasArtistRosterAccess(user) {
  if (isWhiteLabelArtistPortalUser(user)) return false;
  if (INTERNAL_ROLES.has(user?.role)) return true;
  if (isLabelTeamMember(user) || ['label_owner', 'label_manager'].includes(user?.role)) return true;
  const plan = getEntitlementPlan(user);
  if (!ROSTER_ENABLED_PLANS.has(plan)) return false;
  if (SELF_SERVE_PAID_PLANS.has(plan)) {
    return hasPaidSubscription(user) && user?.active_paid_plan === plan;
  }
  return true;
}

export function hasVideoDistributionAccess(user) {
  if (!TRB_VIDEO_AVAILABLE) return false;
  if (INTERNAL_ROLES.has(user?.role)) return true;
  return Boolean(user?.role) && user.role !== 'viewer';
}

export function hasBulkUploadAccess(user) {
  if (INTERNAL_ROLES.has(user?.role)) return true;
  return Boolean(user?.bulk_upload_enabled);
}

export function isPortalSuperAdmin(user) {
  return PORTAL_SUPER_ADMIN_EMAILS.has(String(user?.email || '').trim().toLowerCase());
}

export function isWhiteLabelArtistPortalUser(user) {
  if (!user || isPortalSuperAdmin(user)) return false;
  if (INTERNAL_ROLES.has(user?.role)) return false;
  const role = String(user?.role || '').trim().toLowerCase();
  return Boolean(user?.white_label_signup || user?.white_label_owner_key || user?.white_label_portal_domain)
    && ['artist', 'viewer'].includes(role);
}

export function hasEnterprisePortalAccess(user) {
  if (isPortalSuperAdmin(user)) return true;
  if (INTERNAL_ROLES.has(user?.role)) return true;
  // White-label artists get the normal client dashboard. Ignore the legacy
  // enterprise flags that older portal signups may still have on their record.
  if (isWhiteLabelArtistPortalUser(user)) return false;
  const plan = getEntitlementPlan(user);
  if (plan === 'enterprise') return true;
  const explicitEnterprise = ['plan', 'active_paid_plan', 'current_plan', 'subscription_plan', 'plan_type']
    .some((field) => String(user?.[field] || '').trim().toLowerCase() === 'enterprise');
  return explicitEnterprise || Boolean(user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner);
}

export function hasApiPortalAccess(user) {
  if (INTERNAL_ROLES.has(user?.role)) return true;
  if (isWhiteLabelArtistPortalUser(user)) return false;
  const explicitEnterprise = ['plan', 'active_paid_plan', 'current_plan', 'subscription_plan', 'plan_type']
    .some((field) => String(user?.[field] || '').trim().toLowerCase() === 'enterprise');
  if (explicitEnterprise || user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner) return true;
  const plan = getEntitlementPlan(user);
  if (plan === 'label') {
    return isLabelTeamMember(user) || Boolean(user?.has_paid_subscription && user?.active_paid_plan === 'label');
  }
  return Boolean(plan === 'api_portal' && user?.has_paid_subscription && user?.active_paid_plan === 'api_portal');
}

export function hasPrioritySupportAccess(user) {
  if (isWhiteLabelArtistPortalUser(user)) return false;
  if (INTERNAL_ROLES.has(user?.role)) return true;
  if (isLabelTeamMember(user)) return true;
  const explicitEnterprise = ['plan', 'active_paid_plan', 'current_plan', 'subscription_plan', 'plan_type']
    .some((field) => String(user?.[field] || '').trim().toLowerCase() === 'enterprise');
  if (explicitEnterprise || user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner) return true;
  const plan = getEntitlementPlan(user);
  if (plan === 'label') return true;
  if (plan === 'api_portal') return Boolean(user?.has_paid_subscription && user?.active_paid_plan === 'api_portal');
  return false;
}

export function hasFreeArtistWebsiteAccess(user) {
  if (INTERNAL_ROLES.has(user?.role)) return true;
  if (isWhiteLabelArtistPortalUser(user)) return false;
  if (isLabelTeamMember(user)) return true;
  const plan = getEntitlementPlan(user);
  if (plan === 'label' || plan === 'enterprise') return true;
  const explicitEnterprise = ['plan', 'active_paid_plan', 'current_plan', 'subscription_plan', 'plan_type']
    .some((field) => String(user?.[field] || '').trim().toLowerCase() === 'enterprise');
  if (explicitEnterprise) return true;
  return Boolean(user?.enterprise_portal_enabled || user?.enterprise_access_enabled || user?.enterprise_partner);
}

export function hasArtistWebsiteAccess(user) {
  if (hasFreeArtistWebsiteAccess(user)) return true;
  const plan = getEntitlementPlan(user);
  return plan === 'artist' || user?.role === 'artist';
}
